import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function AuthPage({ mode }) {
  const [params] = useSearchParams();
  const presetRole = params.get("role") || "policyholder";

  const isLogin = mode === "login";
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    role: presetRole,
    phone: "",
    zip_code: "",
    company_name: "",
  });
  const [loading, setLoading] = useState(false);

  // Only redirect on initial mount if the user is already logged in
  // (do not interfere with the explicit submit-time navigation).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user) {
      const dest =
        user.role === "admin"
          ? "/admin"
          : user.role === "contractor"
          ? "/contractor"
          : "/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (isLogin) {
        u = await login(form.email, form.password);
      } else {
        u = await register({
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          role: form.role,
          phone: form.phone || null,
          zip_code: form.zip_code || null,
          company_name: form.company_name || null,
        });
      }
      toast.success(isLogin ? "Welcome back" : "Account created");
      const dest =
        u.role === "admin"
          ? "/admin"
          : u.role === "contractor"
          ? "/contractor"
          : !isLogin
          ? "/onboarding"
          : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-12" data-testid="auth-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E5E7EB] p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[#1E3F33]" />
          <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight">
            {isLogin ? "Sign in" : "Create your account"}
          </h1>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <Label htmlFor="full_name">Full name</Label>
                <Input
                  id="full_name"
                  data-testid="auth-fullname-input"
                  value={form.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>I am a</Label>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger className="mt-1" data-testid="auth-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="policyholder">
                      Homeowner / Business owner
                    </SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "contractor" && (
                <>
                  <div>
                    <Label htmlFor="company_name">Company</Label>
                    <Input
                      id="company_name"
                      data-testid="auth-company-input"
                      value={form.company_name}
                      onChange={(e) => set("company_name", e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zip_code">Service zip code</Label>
                    <Input
                      id="zip_code"
                      data-testid="auth-zip-input"
                      value={form.zip_code}
                      onChange={(e) => set("zip_code", e.target.value)}
                      placeholder="33101"
                      className="mt-1"
                    />
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  data-testid="auth-phone-input"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1"
                />
              </div>
            </>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="auth-email-input"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="auth-password-input"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="btn-primary w-full rounded-full mt-2"
            data-testid="auth-submit-button"
          >
            {loading
              ? "Please wait…"
              : isLogin
              ? "Sign in"
              : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-gray-600 text-center mt-6">
          {isLogin ? (
            <>
              New to ClaimScope?{" "}
              <Link
                to="/signup"
                className="text-[#1E3F33] font-medium hover:underline"
                data-testid="switch-to-signup-link"
              >
                Create account
              </Link>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-[#1E3F33] font-medium hover:underline"
                data-testid="switch-to-login-link"
              >
                Sign in
              </Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
